import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { AuditHistory } from "@/components/audit/AuditHistory";
import { SupplierDeliveryScheduleEditor } from "@/components/suppliers/SupplierDeliveryScheduleEditor";
import { SupplierOpenOrdersPanel } from "@/components/suppliers/SupplierOpenOrdersPanel";
import { api } from "@/lib/apiClient";
import type { Supplier, SupplierDeliverySchedule } from "@/types/camping";

export interface EditSupplierModalProps {
  open: boolean;
  supplier: Supplier | null;
  onClose: () => void;
  onSaved: () => void;
  canEdit: boolean;
}

export function EditSupplierModal({
  open,
  supplier,
  onClose,
  onSaved,
  canEdit,
}: EditSupplierModalProps) {
  const [form, setForm] = useState({
    name: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
    is_active: true,
    orders_enabled: false,
    delivery_schedules: [] as SupplierDeliverySchedule[],
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !supplier) return;
    setForm({
      name: supplier.name,
      contact_person: supplier.contact_person ?? "",
      email: supplier.email ?? "",
      phone: supplier.phone ?? "",
      address: supplier.address ?? "",
      notes: supplier.notes ?? "",
      is_active: supplier.is_active,
      orders_enabled: supplier.orders_enabled,
      delivery_schedules: supplier.delivery_schedules.map((schedule) => ({
        weekday: schedule.weekday,
        cutoff_days_before: schedule.cutoff_days_before,
        cutoff_time: schedule.cutoff_time,
      })),
    });
    setError(null);
    setSubmitting(false);
  }, [open, supplier]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!supplier || !canEdit) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.patch(`/api/v1/suppliers/${supplier.id}`, {
        name: form.name.trim(),
        contact_person: form.contact_person.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
        is_active: form.is_active,
        orders_enabled: form.orders_enabled,
        delivery_schedules: form.delivery_schedules,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  if (!supplier) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={supplier.name}
      description={canEdit ? "Lieferantenstamm bearbeiten" : "Lieferantendetails (nur Lesen)"}
      size="lg"
    >
      <div className="space-y-6">
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
          <fieldset className="space-y-4" disabled={!canEdit}>
            <label className="flex flex-col gap-1 text-sm">
              Name
              <input
                required
                className="eh-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Ansprechpartner
              <input
                className="eh-input"
                value={form.contact_person}
                onChange={(e) => setForm((f) => ({ ...f, contact_person: e.target.value }))}
              />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                E-Mail
                <input
                  type="email"
                  className="eh-input"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Telefon
                <input
                  className="eh-input"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </label>
            </div>
            <label className="flex flex-col gap-1 text-sm">
              Adresse
              <textarea
                rows={2}
                className="eh-input"
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Notizen
              <textarea
                rows={2}
                className="eh-input"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
              Aktiv
            </label>
          </fieldset>

          <SupplierDeliveryScheduleEditor
            enabled={form.orders_enabled}
            schedules={form.delivery_schedules}
            disabled={!canEdit}
            onEnabledChange={(orders_enabled) => setForm((f) => ({ ...f, orders_enabled }))}
            onSchedulesChange={(delivery_schedules) =>
              setForm((f) => ({ ...f, delivery_schedules }))
            }
          />

          {error ? <p className="text-sm text-status-red">{error}</p> : null}
          <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              {canEdit ? "Abbrechen" : "Schließen"}
            </Button>
            {canEdit ? (
              <Button type="submit" loading={submitting}>
                Speichern
              </Button>
            ) : null}
          </div>
        </form>

        <AuditHistory entityType="supplier" entityId={supplier.id} active={open} />

        {supplier.orders_enabled ? (
          <SupplierOpenOrdersPanel supplierId={supplier.id} canSend={canEdit} />
        ) : null}
      </div>
    </Modal>
  );
}
